import type { Placement } from '@floating-ui/react';
import type { DropdownOption, RenderOption } from 'components/DropdownBuilder';
import DropdownBuilder, { defaultProps } from 'components/DropdownBuilder';
import Popover2 from 'components/popovers/Popover2';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { makeOptionSelection } from 'utils/filter-list';
import styles from './PopoverDropdownBuilder.module.scss';

interface Props<T extends DropdownOption<number>> {
  focusRefOnBlur?: {
    current: HTMLElement | null;
  };
  target: (props: { isOpen: boolean; onClose?: () => void }) => React.ReactElement;
  onChange: (
    values: Array<number>,
    setIsOpen?: React.Dispatch<React.SetStateAction<boolean>>
  ) => void;
  renderOption?: RenderOption<T>;
  singleSelect: boolean;
  singleChildSelect: boolean;
  closeOnChange: boolean;
  value?: number | null;
  values?: Array<number>;
  options: Array<T> | (() => Array<T>);
  rootOptionIsGroup?: boolean;
  inputActions?: React.ReactNode;
  highlightSelection?: boolean;
  popoverFixed?: boolean;
  disabled?: boolean;
  maxHeight?: number;
  position?: Placement;
  onOpenChange?: (isOpen: boolean) => void;
}

const PopoverDropdownOptions = <T extends DropdownOption<number>>(props: Props<T>) => {
  const [isOpen, setIsOpen] = useState(false);
  const {
    options,
    value,
    target,
    renderOption,
    values,
    onChange,
    singleSelect,
    singleChildSelect,
    closeOnChange,
    rootOptionIsGroup,
    inputActions,
    highlightSelection,
    popoverFixed,
    disabled,
    maxHeight,
    position,
    onOpenChange,
  } = props;

  useEffect(() => {
    onOpenChange?.(isOpen);
  }, [isOpen, onOpenChange]);

  const handleSetIsOpen = useCallback((isOpen: boolean) => {
    setIsOpen(isOpen);
  }, []);

  const handleOpen = useCallback(() => {
    handleSetIsOpen(true);
  }, [handleSetIsOpen]);

  const handleClose = useCallback(() => {
    handleSetIsOpen(false);
  }, [handleSetIsOpen]);

  const handleChange = useCallback(
    (_optionId: unknown, option: T | null) => {
      // Return _all_ ids, especially useful for multi-select, parent+child selection scenarios
      // This should maybe be moved to DropdownBuilder, but need to verify compatibility with
      // existing uses first.
      const ids = makeOptionSelection(option, {
        options: typeof options === 'function' ? options() : options,
        values: values ?? (value != null ? [value] : []),
        singleSelect,
        singleChildSelect,
      });

      onChange(ids, setIsOpen);
      if (closeOnChange) {
        handleClose();
      }
    },
    [closeOnChange, handleClose, onChange, options, singleChildSelect, singleSelect, value, values]
  );

  const valueProps: {
    values: Array<number> | undefined;
    value: number | null;
  } = { values, value: null };
  if (typeof value !== 'undefined') {
    valueProps.value = value;
  }

  const targetElement = useMemo(
    () => target({ isOpen, onClose: handleClose }),
    [isOpen, target, handleClose]
  );

  return (
    <div className={styles.listWrapper} style={{ height: '100%', width: '100%' }}>
      <Popover2
        isOpen={isOpen}
        onOpen={handleOpen}
        onClose={handleClose}
        target={targetElement}
        closeOnEsc={false}
        closeOnOutsideClick={false}
        fixed={popoverFixed}
        position={position}
      >
        <DropdownBuilder
          renderOption={renderOption}
          {...valueProps}
          options={options}
          onChange={handleChange}
          isOpen
          onSetIsOpen={handleSetIsOpen}
          inputCaret={false}
          rootOptionIsGroup={rootOptionIsGroup}
          inputActions={inputActions}
          highlightSelection={highlightSelection}
          disabled={disabled}
          maxHeight={maxHeight}
        >
          {context => (
            <div>
              <div className={styles.searchInput}>{context.renderInput({ autoFocus: true })}</div>
              {context.renderList()}
            </div>
          )}
        </DropdownBuilder>
      </Popover2>
    </div>
  );
};

PopoverDropdownOptions.defaultProps = {
  ...defaultProps,
  placeholder: '...',
  options: [],
  target: () => <>I am target</>,
  singleSelect: false,
  singleChildSelect: false,
  closeOnChange: false,
  disabled: false,
};

export default PopoverDropdownOptions;
